import React, { useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Skeleton from '@mui/material/Skeleton'
import Switch from '@mui/material/Switch'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import { useSnackbar } from 'notistack'
import { useSetEnabledMutation } from '../../redux/internalAlertsApiSlice'
import HealthBadge from './HealthBadge'
import ConfirmDialog from './ConfirmDialog'
import { ATTENTION_STATES, PROBLEM_STATES, cadenceLabel, channelMeta, count, headerLabel, healthMeta, isLocked, recipientAddress, relTime, runSummary, shortTime } from './alertUtils'
import { BAR_BG, INK, LINE, MUTED, PRIMARY, RED, QuickChip, Th, cardSx, inputSx } from './ui'

const COLS = [
  { key: 'displayName', label: 'Alert' },
  { key: 'enabled', label: 'Enabled' },
  { key: 'activeRecipientCount', label: 'Recipients', right: true },
  { key: 'expectedCadence', label: 'Expected' },
  { key: 'lastRunAt', label: 'Last run' },
  { key: 'health', label: 'Health' },
  { key: 'actions', label: '' },
]
const HEALTH_SEVERITY = { MISSED: 5, FAILING: 5, PARTIAL: 4, LATE: 3, NEVER_SENT: 2, DISABLED: 1, PENDING: 1, OK: 0, NOT_TRACKED: 0 }
const CHIPS = [
  { id: 'attention', label: 'Needs attention', danger: true, test: d => PROBLEM_STATES.includes(d.health?.state) || ATTENTION_STATES.includes(d.health?.state) },
  { id: 'off', label: 'Switched off', test: d => !d.enabled },
]

/**
 * The catalogue of one channel: one row per internal alert, worst health first, with a search box and quick chips.
 * Row click opens the detail dialog; the switch turns the alert on/off (with confirmation); the clock jumps to its
 * message history. WhatsApp rows still sent by Control Tower have their switch locked (the backend rejects edits
 * there too); the dialog explains why. Email rows show their To / CC / BCC split in the recipients tooltip.
 */
export default function AlertCatalogueTab({ defs = [], channel = 'WHATSAPP', loading, onOpen, onShowHistory }) {
  const meta = channelMeta(channel)
  const email = channel === 'EMAIL'
  const { enqueueSnackbar } = useSnackbar()
  const [search, setSearch] = useState('')
  const [chips, setChips] = useState(new Set())
  const [orderBy, setOrderBy] = useState('health')
  const [order, setOrder] = useState('desc')
  const [toggle, setToggle] = useState(null)   // { def, enabled }
  const [setEnabled, { isLoading: toggling }] = useSetEnabledMutation()

  const toggleChip = (id) => setChips(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n })

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    const filtered = defs.filter(d =>
      CHIPS.every(c => !chips.has(c.id) || c.test(d)) &&
      (!q || (d.displayName || '').toLowerCase().includes(q) || (d.id || '').toLowerCase().includes(q) || (d.description || '').toLowerCase().includes(q) ||
        (d.legacyPropertyKey || '').toLowerCase().includes(q) ||
        (d.recipients || []).some(r => (r.displayName || '').toLowerCase().includes(q) || recipientAddress(r).toLowerCase().includes(q)))
    )
    const dir = order === 'asc' ? 1 : -1
    const val = (d) => {
      switch (orderBy) {
        case 'health': return HEALTH_SEVERITY[d.health?.state] ?? 2
        case 'lastRunAt': return d.lastRun?.at || ''
        case 'expectedCadence': return d.expectedCadence ? `${d.expectedCadence.type}${d.expectedCadence.windowStartIst || ''}` : ''
        case 'enabled': return d.enabled ? 1 : 0
        case 'activeRecipientCount': return d.activeRecipientCount ?? 0
        default: return d[orderBy] ?? ''
      }
    }
    return [...filtered].sort((a, b) => {
      const av = val(a), bv = val(b)
      if (av === bv) return (a.displayName || '').localeCompare(b.displayName || '')
      return dir * (typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv)))
    })
  }, [defs, search, chips, orderBy, order])

  const onSort = (col) => { if (orderBy === col) setOrder(o => (o === 'asc' ? 'desc' : 'asc')); else { setOrderBy(col); setOrder('desc') } }

  const confirmToggle = async (reason) => {
    const t = toggle
    setToggle(null)
    try {
      await setEnabled({ templateName: t.def.id, enabled: t.enabled, reason }).unwrap()
      enqueueSnackbar(t.enabled ? `${t.def.displayName} switched on — next run will send` : `${t.def.displayName} switched off — next run will be skipped`, { variant: 'success' })
    } catch (err) {
      enqueueSnackbar(err?.data?.message || err?.message || 'Could not change the alert', { variant: err?.status === 409 ? 'warning' : 'error', autoHideDuration: 8000 })
    }
  }

  if (loading && !defs.length) return <Box sx={{ p: 2 }}>{[...Array(8)].map((_, i) => <Skeleton key={i} height={40} sx={{ mb: 0.5 }} />)}</Box>

  return (
    <Box sx={cardSx}>
      {/* ── Filter bar ── */}
      <Box sx={{ background: BAR_BG, px: 2, py: 1.1, borderBottom: `1px solid ${LINE}`, display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
        <FilterAltOutlinedIcon sx={{ fontSize: 16, color: '#bbb', flexShrink: 0 }} />
        <TextField size='small' placeholder={email ? 'Alert, key, property or address' : 'Alert, template or recipient'} value={search} onChange={(e) => setSearch(e.target.value)} sx={{ ...inputSx, width: 280 }}
          InputProps={{ startAdornment: <InputAdornment position='start'><SearchOutlinedIcon sx={{ fontSize: 16, color: MUTED }} /></InputAdornment> }} />
        <Box sx={{ width: '1px', height: 20, background: '#ddd', mx: 0.25, flexShrink: 0 }} />
        {CHIPS.map(c => <QuickChip key={c.id} on={chips.has(c.id)} danger={c.danger} onClick={() => toggleChip(c.id)}>{c.label}</QuickChip>)}
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: '0.7rem', color: MUTED }}>{count(rows.length)} of {count(defs.length)} · worst health first</Typography>
      </Box>

      <TableContainer sx={{ overflowX: 'auto', maxHeight: 640 }}>
        <Table size='small' stickyHeader sx={{ '& td': { fontSize: '0.8rem', borderBottom: `1px solid ${LINE}`, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' } }}>
          <TableHead>
            <TableRow>
              {COLS.map(c => (
                <Th key={c.key} col={c.key === 'actions' ? null : c.key} sortCol={orderBy} sortDir={order} onSort={onSort} right={c.right}>{c.label}</Th>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(d => {
              const locked = isLocked(d)
              const notReached = (d.recipients || []).filter(r => r.active && d.lastRun && !(d.lastRun.recipientsReached || []).includes(recipientAddress(r)))
              return (
                <TableRow key={d.id} hover onClick={() => onOpen?.(d)} sx={{ cursor: 'pointer', '&:hover': { background: '#F9FAFB' } }}>
                  <TableCell sx={{ whiteSpace: 'normal', minWidth: 260, py: 1.1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.84rem', color: d.enabled ? INK : MUTED, lineHeight: 1.25 }}>{d.displayName || d.id}</Typography>
                    <Typography sx={{ fontSize: '0.66rem', color: MUTED, fontFamily: 'monospace' }}>{d.id}</Typography>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Tooltip arrow title={locked ? 'Still sent by Control Tower — on/off is not editable here yet' : d.enabled ? 'On — click to switch off' : 'Off — click to switch on'}>
                      <span><Switch size='small' checked={Boolean(d.enabled)} disabled={toggling || locked} onChange={(e) => setToggle({ def: d, enabled: e.target.checked })} /></span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align='right'>
                    <Tooltip arrow title={
                      (d.recipients || []).length
                        ? <Box component='span' sx={{ whiteSpace: 'pre-line' }}>{(d.recipients || []).map(r => `${r.active ? '•' : '○'} ${r.displayName || recipientAddress(r)}${email && r.displayName ? ` <${r.email}>` : ''}${email ? ` · ${headerLabel(r.headerType)}` : ''}${r.active ? '' : ' (inactive)'}`).join('\n')}</Box>
                        : `No ${meta.recipientNoun === 'address' ? 'addresses' : 'recipients'} configured`
                    }>
                      <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 700 }}>
                        {count(d.activeRecipientCount)}
                        {notReached.length > 0 && <Box component='span' title={`${notReached.length} configured but not reached in the last run`} sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: RED, display: 'inline-block' }} />}
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ color: d.expectedCadence ? INK : MUTED }}>{cadenceLabel(d.expectedCadence)}</TableCell>
                  <TableCell>
                    {d.lastRun?.at ? (
                      <Box>
                        <Typography sx={{ fontSize: '0.8rem' }} title={`${d.lastRun.at} IST`}>{shortTime(d.lastRun.at)} <Box component='span' sx={{ color: MUTED }}>· {relTime(d.lastRun.at)}</Box></Typography>
                        <Typography sx={{ fontSize: '0.68rem', color: d.lastRun.failed ? RED : MUTED }}>{runSummary(d.lastRun)}</Typography>
                      </Box>
                    ) : <Box component='span' sx={{ color: MUTED }}>—</Box>}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: 360 }}>
                      <HealthBadge state={d.health?.state} reason={d.health?.reason} />
                      <Typography noWrap sx={{ fontSize: '0.7rem', color: MUTED, maxWidth: 260 }} title={d.health?.reason || healthMeta(d.health?.state).help}>{d.health?.reason || ''}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align='right' onClick={(e) => e.stopPropagation()}>
                    <Tooltip title='Message history for this alert' arrow>
                      <IconButton size='small' onClick={() => onShowHistory?.(d)} sx={{ color: PRIMARY }}><HistoryOutlinedIcon fontSize='small' /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              )
            })}
            {!rows.length && (
              <TableRow><TableCell colSpan={COLS.length} align='center' sx={{ py: 5, color: MUTED }}>{defs.length ? 'No alert matches the filter' : 'No alert definitions'}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDialog
        open={Boolean(toggle)} busy={toggling} onCancel={() => setToggle(null)} onConfirm={confirmToggle}
        title={toggle ? `${toggle.enabled ? 'Switch on' : 'Switch off'} “${toggle.def.displayName}”?` : ''}
        confirmLabel={toggle?.enabled ? 'Switch on' : 'Switch off'} tone={toggle?.enabled ? 'primary' : 'error'}
        withReason reasonRequired={toggle ? !toggle.enabled : false}
      >
        {toggle?.enabled
          ? <>Proship resumes this alert on its next run, to {count(toggle.def.activeRecipientCount)} active recipient{toggle.def.activeRecipientCount === 1 ? '' : 's'}.</>
          : <>Proship will skip this alert from its next run until it is switched on again. Nobody will receive it.</>}
      </ConfirmDialog>
    </Box>
  )
}
